/**
 * User Role Service
 *
 * API calls for managing user roles and permissions.
 * Uses path-based multi-tenancy: /api/orgs/{orgId}/user-roles
 */
import apiClient from './apiClient';

const DEFAULT_ORG_ID = 1;
const basePath = (orgId = DEFAULT_ORG_ID) => `/api/orgs/${orgId}/user-roles`;

const employeeRoleService = {
  listRoles: async (orgId = DEFAULT_ORG_ID, params = {}) => {
    const response = await apiClient.get(basePath(orgId), { params });
    return response.data;
  },

  searchRoles: async (q, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`${basePath(orgId)}/search`, { params: { q } });
    return response.data;
  },

  getRole: async (roleId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`${basePath(orgId)}/${roleId}`);
    return response.data;
  },

  createRole: async (roleData, orgId) => {
    const resolvedOrgId = orgId || roleData.orgId || DEFAULT_ORG_ID;
    const response = await apiClient.post(basePath(resolvedOrgId), roleData);
    return response.data;
  },

  updateRole: async (roleId, roleData, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.put(`${basePath(orgId)}/${roleId}`, roleData);
    return response.data;
  },

  deleteRole: async (roleId, orgId = DEFAULT_ORG_ID) => {
    await apiClient.delete(`${basePath(orgId)}/${roleId}`);
  },

  getPermissionCatalog: async (orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`${basePath(orgId)}/permission-catalog`);
    return response.data;
  },

  cloneRole: async (roleId, roleData, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.post(`${basePath(orgId)}/${roleId}/clone`, roleData);
    return response.data;
  },

  listUsersForRole: async (roleId, orgId = DEFAULT_ORG_ID, params = {}) => {
    const response = await apiClient.get(`${basePath(orgId)}/${roleId}/users`, { params });
    return response.data;
  },

  assignUsersToRole: async (roleId, userIds, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.post(`${basePath(orgId)}/${roleId}/users`, { userIds });
    return response.data;
  },

  removeUsersFromRole: async (roleId, userIds, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.delete(`${basePath(orgId)}/${roleId}/users`, { data: { userIds } });
    return response.data;
  },

  replaceUsersForRole: async (roleId, userIds, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.put(`${basePath(orgId)}/${roleId}/users`, { userIds });
    return response.data;
  },

  listRolesForUser: async (userId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`${basePath(orgId)}/users/${userId}/roles`);
    return response.data;
  },

  assignRolesForUser: async (userId, roleIds, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.put(`/api/orgs/${orgId}/users/${userId}/roles`, {
      roleIds: Array.isArray(roleIds) ? roleIds : [],
    });
    return response.data;
  },
};

export default employeeRoleService;
